import type { ReactNode } from "react";
import { PageHeroTitle } from "./PageHeroTitle";

export interface PageHeroCopyProps {
  /** El rótulo pequeño sobre el titular. */
  eyebrow: string;
  /** El titular completo, ya traducido. */
  title: string;
  /** La parte del titular que va resaltada. */
  highlight?: string;
  /** La entradilla bajo el titular. */
  description?: ReactNode;
  /** Los botones. Los pone quien usa la portada, no esta pieza. */
  actions?: ReactNode;
}

/**
 * La columna de texto de una portada de página: rótulo, titular,
 * entradilla y acciones. Siempre alineada a la izquierda.
 *
 * ── Por qué a la izquierda y no centrada ─────────────────────────────
 * Es la mitad del patrón que aprobó Anahi en `/franquicias`: el texto se
 * apoya en el borde izquierdo y la pieza viva ocupa la derecha. Centrar
 * el texto rompe ese eje —el titular tiraría hacia el centro y la pieza
 * de la derecha quedaría descolgada— y deja la portada sin la asimetría
 * que la distingue de un bloque de contenido cualquiera.
 *
 * En el teléfono no hay dos columnas, pero el texto SIGUE a la izquierda:
 * un titular de tres líneas centrado obliga al ojo a buscar el principio
 * de cada línea en un sitio distinto, y es exactamente el tamaño de
 * pantalla donde eso más cuesta.
 *
 * ── El ancho de la entradilla está limitado, el del titular no ───────
 * `max-w-xl` en el párrafo: a 1440 px la columna de texto es ancha, y una
 * línea de 90 caracteres se sigue mal con la vista. El titular sí puede
 * ocupar toda la columna porque son pocas palabras y grandes.
 */
export function PageHeroCopy({
  eyebrow,
  title,
  highlight,
  description,
  actions,
}: PageHeroCopyProps) {
  return (
    <div className="text-left">
      <p className="text-ds-label font-bold uppercase tracking-ds-label-wide text-ds-ink-3">
        {eyebrow}
      </p>

      <PageHeroTitle highlight={highlight} className="mt-6">
        {title}
      </PageHeroTitle>

      {description && (
        <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-ds-ink-3">
          {description}
        </p>
      )}

      {actions && <div className="mt-9 flex flex-wrap gap-3">{actions}</div>}
    </div>
  );
}
